import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {structuredTableActionOrigin} from 'analytics/Util';
import {
  ActionMethod,
  ActionOrigin,
  COMMON_ANALYTICS_ACTIONS,
  CommonAnalyticsActions,
  ELN_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';
import {ColumnDataType, guessColumnDataType} from 'notebook/structured-tables/columns/ColumnDataType';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {getStructuredTableAdapterFromTable} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTableColumn,
  StructuredTableKind,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import addPrefixSuffixToObjectKeys, {WithKeyPrefixSuffix} from 'util/addPrefixSuffixToObjectKeys';

export const STRUCTURED_TABLE_MAPPING_CONFIG_MODAL_TYPE = 'structured_table_mapping_config';

/** Send an Analytics event for opening, closing, or submitting the Table Mapping Config Modal */
export function trackStructuredTableMappingConfigModalEvent(
  sessionId: string,
  action:
    | CommonAnalyticsActions.OPENED_MODAL
    | CommonAnalyticsActions.CLOSED_MODAL
    | CommonAnalyticsActions.SUBMITTED_MODAL,
  fileId: string,
  originatingTable: UnknownStructuredTable,
  extraFields?: {
    isConfig?: boolean;
    hadChanges?: boolean;
  }
): void {
  analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS[action], ANALYTICS_MODULE.ELN, {
    sessionId,
    actionOrigin: structuredTableActionOrigin(originatingTable),
    modalType: STRUCTURED_TABLE_MAPPING_CONFIG_MODAL_TYPE,
    fileId,
    ...extraFields,
  });
}

export interface StructuredTableMappingAnalyticsPayload {
  sourceTableProvenanceUuid: string;
  sourceTableKind: StructuredTableKind;
  destinationTableProvenanceUuid: string;
  destinationTableKind: StructuredTableKind;
  replicationFactor?: number;
  columnMappings: Array<StructuredTableColumnMappingAnalyticsPayload>;
}

interface ColumnInfoAnalyticsPayloadSuffix {
  columnProvenanceUuid?: string; // always defined for TM, sometimes undefined for AFST
  columnType?: TableColumnType; // always defined for TM, sometimes undefined for AFST
  columnDataType?: ColumnDataType;
  columnSchemaId?: string | null;
  columnIsMulti?: boolean;
  columnIsReadOnly: boolean | null;
}

type ColumnInfoAnalyticsPayload<P extends 'source' | 'destination'> = WithKeyPrefixSuffix<
  ColumnInfoAnalyticsPayloadSuffix,
  P,
  ''
>;

type StructuredTableColumnMappingAnalyticsPayload = ColumnInfoAnalyticsPayload<'source'> &
  ColumnInfoAnalyticsPayload<'destination'>;

/** Send an Analytics event containing all the StructuredTableMappings in an Entry/Template/Document after
 * updating mappings */
export function trackUpdateStructuredTableMappingsEvent(
  actionMethod: ActionMethod,
  fileId: string,
  isConfig: boolean | undefined,
  structuredTableMappings: Array<StructuredTableMapping>,
  structuredTables: Array<UnknownStructuredTable>,
  originatingTable: UnknownStructuredTable | null
): void {
  const tableMappingAnalyticsPayloads: Array<StructuredTableMappingAnalyticsPayload> =
    structuredTableMappings.map((tableMapping): StructuredTableMappingAnalyticsPayload => {
      const sourceTable = structuredTables.find(
        (table) => table.provenanceUuid === tableMapping.sourceTableProvenanceUuid
      )!;
      const destinationTable = structuredTables.find(
        (table) => table.provenanceUuid === tableMapping.destinationTableProvenanceUuid
      )!;
      return convertStructuredTableMappingToAnalyticsPayload(tableMapping, sourceTable, destinationTable);
    });
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.UPDATED_TABLE_MAPPINGS, {
    actionOrigin: originatingTable ? structuredTableActionOrigin(originatingTable) : ActionOrigin.NOTEBOOK,
    actionMethod,
    fileId,
    isConfig,
    mappings: tableMappingAnalyticsPayloads,
    numStructuredTables: structuredTables.length,
  });
}

/** Send an Analytics event when performing a StructuredTableMapping */
export function trackPerformStructuredTableMappingEvent(
  sessionId: string,
  actionMethod: ActionMethod,
  fileId: string,
  structuredTableMapping: StructuredTableMapping,
  sourceTable: UnknownStructuredTable,
  destinationTable: UnknownStructuredTable,
  extraData: {
    sourceFirstSubmit: boolean;
    destinationNumOriginalRows: number;
    destinationNumFinalRows: number;
    destinationNumRowsAdded: number;
  }
): void {
  analytics.trackEvent(ELN_ANALYTICS_ACTIONS.PERFORMED_TABLE_MAPPING, {
    sessionId,
    actionOrigin: structuredTableActionOrigin(sourceTable),
    actionMethod,
    fileId,
    ...convertStructuredTableMappingToAnalyticsPayload(structuredTableMapping, sourceTable, destinationTable),
    ...extraData,
  });
}

export function convertColumnToAnalyticsPayload<T extends 'source' | 'destination'>(
  sourceOrDestination: T,
  column: StructuredTableColumn | null,
  columnDefinition: ColumnDefinition | null
): ColumnInfoAnalyticsPayload<T> {
  const columnDataType = columnDefinition ? guessColumnDataType(columnDefinition) : null;

  const payload: ColumnInfoAnalyticsPayloadSuffix = {
    columnProvenanceUuid: column?.provenanceUuid,
    columnType: column?.columnType,
    columnDataType: columnDataType?.type,
    columnSchemaId: columnDataType?.schemaId,
    columnIsMulti: columnDataType?.isMulti,
    columnIsReadOnly: column ? isReadOnly(column) : null,
  };
  return addPrefixSuffixToObjectKeys(payload, sourceOrDestination, '');
}

export function convertStructuredTableColumnsToAnalyticsPayload(
  sourceColumn: StructuredTableColumn | null,
  sourceColumnDefinition: ColumnDefinition | null,
  destinationColumn: StructuredTableColumn | null,
  destinationColumnDefinition: ColumnDefinition | null
): StructuredTableColumnMappingAnalyticsPayload {
  return {
    ...convertColumnToAnalyticsPayload('source', sourceColumn, sourceColumnDefinition),
    ...convertColumnToAnalyticsPayload('destination', destinationColumn, destinationColumnDefinition),
  };
}

function convertStructuredTableMappingToAnalyticsPayload(
  tableMapping: StructuredTableMapping,
  sourceTable: UnknownStructuredTable,
  destinationTable: UnknownStructuredTable
): StructuredTableMappingAnalyticsPayload {
  const sourceColumnDefinitions =
    getStructuredTableAdapterFromTable(sourceTable).getColumnDefinitions(sourceTable);
  const destinationColumnDefinitions =
    getStructuredTableAdapterFromTable(destinationTable).getColumnDefinitions(destinationTable);
  return {
    sourceTableProvenanceUuid: tableMapping.sourceTableProvenanceUuid,
    sourceTableKind: sourceTable.kind,
    destinationTableProvenanceUuid: tableMapping.destinationTableProvenanceUuid,
    destinationTableKind: destinationTable.kind,
    replicationFactor: tableMapping.replicationFactor,
    columnMappings: tableMapping.structuredTableColumnMappings.map(
      (columnMapping): StructuredTableColumnMappingAnalyticsPayload => {
        const sourceColumnIndex = sourceTable.columns.findIndex(
          (column) => column.provenanceUuid === columnMapping.sourceTableColumnProvenanceUuid
        );
        const destinationColumnIndex = destinationTable.columns.findIndex(
          (column) => column.provenanceUuid === columnMapping.destinationTableColumnProvenanceUuid
        );
        return convertStructuredTableColumnsToAnalyticsPayload(
          sourceTable.columns[sourceColumnIndex],
          sourceColumnDefinitions[sourceColumnIndex],
          destinationTable.columns[destinationColumnIndex],
          destinationColumnDefinitions[destinationColumnIndex]
        );
      }
    ),
  };
}
